/**
 * The fly scope: the home board's specimen, live in three.js.
 *
 * A housefly body (web/public/fly/housefly.glb, re-rigged from the owner's model in
 * Blender: glass cuticle, red eyes, its own `stay` and `fly` actions) carries the
 * real BANC v888 connectome inside its head and thorax - every one of the 169,088
 * cells at its own position, scaled from nanometres into the body. Nothing on the
 * stage is a recording: the brain reacts to what the chain says the fly did.
 *
 *   feed  (a burn on sensory channel c)  -> the fly grooms; the cells the kernel
 *         charges for that channel (n < 256, n % 60 === c) light up
 *   thought (a BrainState event)        -> the fired cells strike, and a signal runs
 *         down each of their real synapses (edges_*.bin) to the cells it charges
 *   motor output                        -> a thought that fired descending neurons
 *         lifts the wings; any other thought only flicks them
 *
 * The camera tours the specimen like the reference clip (surface, eyes, cuticle,
 * brain, optic lobe, mushroom body, back out), and a second, smaller view turns the
 * whole specimen for the board's observation card.
 */

import {
  ACESFilmicToneMapping,
  AnimationMixer,
  type AnimationAction,
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  Color,
  DirectionalLight,
  DoubleSide,
  HemisphereLight,
  NoColorSpace,
  Group,
  LoopRepeat,
  Material,
  MathUtils,
  Mesh,
  MeshBasicMaterial,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  type Object3D,
  PerspectiveCamera,
  PlaneGeometry,
  PMREMGenerator,
  Points,
  Bone,
  SkinnedMesh,
  ShaderMaterial,
  SphereGeometry,
  SRGBColorSpace,
  Scene,
  type Texture,
  TextureLoader,
  Vector3,
  WebGLRenderer,
} from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { clone as cloneSkinned } from "three/examples/jsm/utils/SkeletonUtils.js";

import type { Connectome, NeuronGeometry } from "./geometry";

// ---- where the brain sits in the body ----------------------------------------
// The same placement the Blender scene uses (.blender/housefly_obrain.blend): BANC
// nanometres, scaled into the body's units, the brain's width squeezed to clear the
// eyes. Blender is Z-up and the GLB is Y-up, so (x, y, z) -> (x, z, -y).
const NM = 0.45e-6;

const SQUEEZE_X = 0.66;

const ORIGIN = [504_000, 150_000, 150_000] as const;

const OFFSET_Y = -0.365;

const OFFSET_Z = 0.32;

/** one body unit in millimetres, at the brain's own scale */
export const MM_PER_UNIT = 1 / (NM * 1e6);

const fromBlender = (x: number, y: number, z: number): Vector3 => new Vector3(x, z, -y);

/** micron positions (geometry.ts: x, -y, z) -> the body's own space */
function placeNeuron(positions: Float32Array, index: number, out: Float32Array, at: number): void {
  const xNm = positions[index * 3] * 1000;
  const yNm = -positions[index * 3 + 1] * 1000;
  const zNm = positions[index * 3 + 2] * 1000;
  const bx = SQUEEZE_X * (xNm - ORIGIN[0]) * NM;
  const by = OFFSET_Y + (yNm - ORIGIN[1]) * NM;
  const bz = OFFSET_Z - (zNm - ORIGIN[2]) * NM;

  out[at] = bx;
  out[at + 1] = bz;
  out[at + 2] = -by;
}

// ---- the tour ------------------------------------------------------------------
export type StopKey = "specimen" | "eyes" | "cuticle" | "brain" | "optic" | "memory" | "map";

type Stop = {
  readonly key: StopKey;
  /** what the camera looks at, Blender coordinates */
  readonly target: readonly [number, number, number];
  readonly az: number;
  readonly el: number;
  readonly dist: number;
  /** cuticle opacity while the camera holds here */
  readonly cuticle: number;
};

// anatomical anchors, Blender coordinates; the brain ones are medians of the real
// cells (central brain, the right optic lobe, the right Kenyon cells)
const T_BODY = [0, -0.05, 0.27] as const;

const T_EYE = [-0.17, -0.44, 0.34] as const;

const T_BRAIN = [-0.0068, -0.3666, 0.3162] as const;

const T_OPTIC = [-0.0926, -0.3619, 0.3408] as const;

const T_MEMORY = [-0.039, -0.3555, 0.2792] as const;

const T_ANTENNA = [-0.035, -0.52, 0.37] as const;

export const STOPS: readonly Stop[] = [
  { key: "specimen", target: T_BODY, az: 214, el: 18, dist: 1.85, cuticle: 1 },
  { key: "eyes", target: T_EYE, az: 203, el: 12, dist: 0.62, cuticle: 1 },
  { key: "cuticle", target: T_BRAIN, az: 240, el: 28, dist: 0.95, cuticle: 0.35 },
  { key: "brain", target: T_BRAIN, az: 266, el: 12, dist: 0.42, cuticle: 0 },
  { key: "optic", target: T_OPTIC, az: 197, el: 10, dist: 0.42, cuticle: 0 },
  { key: "memory", target: T_MEMORY, az: 250, el: 40, dist: 0.38, cuticle: 0 },
  { key: "map", target: T_BODY, az: 207, el: 18, dist: 1.95, cuticle: 1 },
];

const MOVE_SECONDS = 1.6;

const HOLD_SECONDS = 1.9;

const ORBIT_DEG_PER_SECOND = 2.6;

export const ANCHORS = {
  eye: fromBlender(...T_EYE),
  antenna: fromBlender(...T_ANTENNA),
  brain: fromBlender(...T_BRAIN),
  optic: fromBlender(...T_OPTIC),
  memory: fromBlender(...T_MEMORY),
} as const;

type CameraPose = { readonly position: Vector3; readonly target: Vector3 };

function cameraAt(stop: Stop, orbit: number): CameraPose {
  const [tx, ty, tz] = stop.target;
  const a = MathUtils.degToRad(stop.az + orbit);
  const e = MathUtils.degToRad(stop.el);
  const bx = tx + stop.dist * Math.cos(e) * Math.cos(a);
  const by = ty + stop.dist * Math.cos(e) * Math.sin(a);
  const bz = tz + stop.dist * Math.sin(e);

  return { position: fromBlender(bx, by, bz), target: fromBlender(tx, ty, tz) };
}

const ease = (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

// ---- the cells -----------------------------------------------------------------
// The reference's confetti: each cell its own saturated colour, seeded by its index
// so a neuron keeps its colour across visits. A strike lights the cell in its own
// colour before it whitens; the field adds a faint charge glow near the trigger.
const PALETTE = [
  [0.02, 0.42, 0.98],
  [0.98, 0.1, 0.45],
  [1.0, 0.8, 0.0],
  [0.0, 0.78, 0.7],
  [0.45, 0.18, 0.95],
  [1.0, 0.36, 0.05],
  [0.3, 0.85, 0.12],
  [0.98, 0.45, 0.75],
] as const;

const hash = (n: number): number => {
  let h = Math.imul(n ^ 0x9e3779b9, 0x85ebca6b);

  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);

  return (h ^ (h >>> 16)) >>> 0;
};

const CELL_VERTEX = /* glsl */ `
  attribute vec3 color;
  attribute vec3 hue;
  attribute float fire;
  attribute float hit;
  attribute float charge;
  uniform float uTime;
  uniform float uScale;
  uniform float uSize;
  varying vec3 vColor;
  varying vec3 vHue;
  varying float vHeat;
  varying float vHit;
  void main() {
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    float strike = fire > 0.0 && uTime >= fire ? exp(-(uTime - fire) * 0.55) : 0.0;
    float reach = hit > 0.0 && uTime >= hit ? exp(-(uTime - hit) * 0.9) : 0.0;
    vHeat = strike;
    vHit = max(reach, charge);
    vColor = color;
    vHue = hue;
    gl_PointSize = max(0.8, uSize * (1.0 + strike * 3.2 + vHit * 0.9) * uScale / -mv.z);
    gl_Position = projectionMatrix * mv;
  }
`;

const CELL_FRAGMENT = /* glsl */ `
  varying vec3 vColor;
  varying vec3 vHue;
  varying float vHeat;
  varying float vHit;
  void main() {
    vec2 c = gl_PointCoord * 2.0 - 1.0;
    float r2 = dot(c, c);
    if (r2 > 1.0) discard;
    vec3 n = vec3(c.x, -c.y, sqrt(1.0 - r2));
    float light = 0.5 + 0.55 * max(dot(n, normalize(vec3(0.35, 0.65, 0.7))), 0.0);
    vec3 col = vColor * light;
    col = mix(col, vHue, vHit * 0.7);
    col = mix(col, vHue, min(1.0, vHeat * 1.6));
    col = mix(col, vec3(1.0), smoothstep(0.55, 1.0, vHeat) * 0.85);
    gl_FragColor = vec4(col, 1.0);
  }
`;

// a signal is a comet of sprites riding a quadratic curve from a fired cell to a
// cell it synapses onto
const SIGNAL_VERTEX = /* glsl */ `
  attribute vec3 p1;
  attribute vec3 p2;
  attribute float start;
  attribute float lag;
  uniform float uTime;
  uniform float uScale;
  uniform float uSize;
  uniform float uTravel;
  varying float vAlpha;
  void main() {
    float t = clamp((uTime - start) / uTravel - lag, 0.0, 1.0);
    float live = step(start + lag * uTravel, uTime) * step(uTime, start + (1.0 + lag) * uTravel + 0.4);
    float u = 1.0 - t;
    vec3 p = u * u * position + 2.0 * u * t * p1 + t * t * p2;
    vec4 mv = modelViewMatrix * vec4(p, 1.0);
    vAlpha = live * (1.0 - lag * 4.0) * (1.0 - smoothstep(0.85, 1.0, t) * 0.6);
    gl_PointSize = max(2.0, uSize * (1.0 - lag * 3.0) * uScale / -mv.z);
    gl_Position = projectionMatrix * mv;
  }
`;

const SIGNAL_FRAGMENT = /* glsl */ `
  varying float vAlpha;
  void main() {
    vec2 c = gl_PointCoord * 2.0 - 1.0;
    float r2 = dot(c, c);
    if (r2 > 1.0 || vAlpha <= 0.0) discard;
    float core = exp(-r2 * 3.0);
    gl_FragColor = vec4(mix(vec3(0.55, 0.92, 1.0), vec3(1.0), core), vAlpha * core);
  }
`;

// a flare is a halo drawn over the tissue (an X-ray of the event): white for a cell the
// thought fired, cyan for a cell a feed charged or a signal reached
const FLARE_VERTEX = /* glsl */ `
  attribute float start;
  attribute float kind;
  uniform float uTime;
  uniform float uScale;
  uniform float uSize;
  varying float vAlpha;
  varying float vKind;
  void main() {
    float age = uTime - start;
    float life = kind > 1.5 ? 3.4 : kind > 0.5 ? 1.6 : 2.8;
    vAlpha = age < 0.0 ? 0.0 : exp(-age * 3.0 / life) * (1.0 - smoothstep(life * 0.8, life, age));
    vKind = kind;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    float grow = kind > 1.5 ? 0.8 + age * 0.9 : kind > 0.5 ? 1.0 : 1.0 + 1.6 * exp(-age * 2.5);
    float scale = kind > 1.5 ? 1.5 : kind > 0.5 ? 0.6 : 1.0;
    gl_PointSize = max(kind > 1.5 ? 26.0 : 10.0, uSize * grow * scale * uScale / -mv.z);
    gl_Position = projectionMatrix * mv;
  }
`;

const FLARE_FRAGMENT = /* glsl */ `
  varying float vAlpha;
  varying float vKind;
  void main() {
    vec2 c = gl_PointCoord * 2.0 - 1.0;
    float r2 = dot(c, c);
    if (r2 > 1.0 || vAlpha <= 0.01) discard;
    float core = exp(-r2 * 9.0);
    float halo = exp(-r2 * 2.2) * 0.55;
    // a feed is a ring spreading from the charged cell
    float ring = vKind > 1.5 ? exp(-pow(sqrt(r2) - 0.7, 2.0) * 90.0) : 0.0;
    vec3 tint = vKind > 0.5 ? vec3(0.2, 0.8, 1.0) : vec3(1.0, 0.97, 0.9);
    gl_FragColor = vec4(mix(tint, vec3(1.0), core), vAlpha * min(1.0, core + halo + ring));
  }
`;

const FLARE_KEEP = 4096;

const TAIL = 9;

const SIGNAL_TRAVEL = 1.4;

const MAX_TARGETS = 18;

// ---- the scope -------------------------------------------------------------------
/** the synapse list sorted by source: a cell's targets are targets[start[c]..start[c + 1]] */
type OutIndex = { readonly start: Int32Array; readonly targets: Uint32Array };

type TourFrame = CameraPose & {
  readonly stop: number;
  readonly moving: boolean;
  readonly progress: number;
  readonly cuticle: number;
  readonly dist: number;
  readonly orbit: number;
};

export type ScopeFrame = {
  readonly stop: StopKey;
  readonly index: number;
  /** 0..1 across the whole tour */
  readonly progress: number;
  readonly moving: boolean;
  readonly zoom: number;
  readonly cuticle: number;
  /** degrees, the orbit angle */
  readonly parallax: number;
  /** pixels per millimetre at the camera's target */
  readonly pxPerMm: number;
};

export type ScopeOptions = {
  readonly mount: HTMLElement;
  readonly observe: HTMLElement | null;
  readonly geometry: NeuronGeometry;
  readonly connectome: Connectome;
  readonly reduced: boolean;
  /** descending-neuron flags per cell (the motor output a thought can carry) */
  readonly descending: Uint8Array;
  readonly onFrame?: (
    frame: ScopeFrame,
    project: (p: Vector3) => [number, number, boolean],
  ) => void;
  readonly onQuality?: (quality: number) => void;
};

type View = {
  readonly renderer: WebGLRenderer;
  readonly scene: Scene;
  readonly camera: PerspectiveCamera;
  readonly host: HTMLElement;
  readonly cells: ShaderMaterial;
  readonly signals: ShaderMaterial;
};

function gradientBackground(): Texture {
  const canvas = document.createElement("canvas");

  canvas.width = 4;
  canvas.height = 256;
  const ctx = canvas.getContext("2d");

  if (ctx !== null) {
    const g = ctx.createLinearGradient(0, 0, 0, 256);

    g.addColorStop(0, "#f4f1fa");
    g.addColorStop(0.55, "#ece7f6");
    g.addColorStop(1, "#f6f4f8");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 4, 256);
  }

  const texture = new CanvasTexture(canvas);

  texture.colorSpace = SRGBColorSpace;

  return texture;
}

function shadowBlob(): Mesh {
  const canvas = document.createElement("canvas");

  canvas.width = canvas.height = 128;
  const ctx = canvas.getContext("2d");

  if (ctx !== null) {
    const g = ctx.createRadialGradient(64, 64, 4, 64, 64, 64);

    g.addColorStop(0, "rgba(60,50,90,0.32)");
    g.addColorStop(1, "rgba(60,50,90,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 128, 128);
  }

  const mesh = new Mesh(
    new PlaneGeometry(1.6, 1.15),
    new MeshBasicMaterial({ map: new CanvasTexture(canvas), transparent: true, depthWrite: false }),
  );

  mesh.rotation.x = -Math.PI / 2;
  mesh.position.set(0, -0.005, 0.05);

  return mesh;
}

const BEAD_PHASE = new WeakMap<Object3D, number>();

function beads(): Group {
  const group = new Group();
  const colors = [0x0b8cbf, 0xc81e6e, 0xf0f0f4, 0xd9a228];
  const sphere = new SphereGeometry(1, 24, 16);

  const mats = colors.map(
    (color) =>
      new MeshPhysicalMaterial({ color, roughness: 0.15, clearcoat: 1, clearcoatRoughness: 0.08 }),
  );

  for (let i = 0; i < 22; i += 1) {
    const r = 0.012 + ((hash(i * 7 + 1) % 1000) / 1000) * 0.02;
    const bead = new Mesh(sphere, mats[i % mats.length]);
    const x = -0.9 + ((hash(i * 3 + 11) % 1000) / 1000) * 1.8;
    const y = -1.0 + ((hash(i * 5 + 17) % 1000) / 1000) * 1.9;
    const z = 0.05 + ((hash(i * 13 + 23) % 1000) / 1000) * 0.9;

    bead.scale.setScalar(r);
    bead.position.copy(fromBlender(x, y, z));

    // the tour flies through the head: a bead there would sit in front of the lens
    const head = fromBlender(...T_BRAIN);
    const away = bead.position.clone().sub(head);

    if (away.length() < 0.55) bead.position.copy(head).add(away.setLength(0.55 + away.length()));
    BEAD_PHASE.set(bead, (hash(i) % 628) / 100);
    group.add(bead);
  }

  return group;
}

/**
 * The model's own surface maps that the GLB does not carry (web/public/fly, 1024px
 * from the original textures): the chitin's roughness and its cavity (the dark of
 * every seam and pit), and the wing membrane's roughness. glTF UVs: no flip.
 */
type SurfaceMaps = {
  readonly rough: Texture;
  readonly cavity: Texture;
  readonly wingRough: Texture;
};

function loadSurfaceMaps(): SurfaceMaps {
  const loader = new TextureLoader();

  const load = (name: string): Texture => {
    const texture = loader.load(`${import.meta.env.BASE_URL}fly/${name}.webp`);

    texture.flipY = false;
    texture.colorSpace = NoColorSpace;

    return texture;
  };

  return {
    rough: load("body_col_rough"),
    cavity: load("body_col_cavaty"),
    wingRough: load("hane_rough"),
  };
}

/** each faded material's own opacity at full cuticle */
const BASE_OPACITY = new WeakMap<Material, number>();

/** the cuticle, the eyes, the hairs and the wings, re-dressed for the board */
function dress(root: Object3D, environment: Texture, maps: SurfaceMaps): Material[] {
  const fading: Material[] = [];
  let bodyMap: Texture | null = null;
  let bodyNormal: Texture | null = null;

  root.traverse((node) => {
    if (!(node instanceof Mesh) || !(node.material instanceof MeshStandardMaterial)) return;

    const source = node.material;

    if (source.name.startsWith("body")) {
      bodyMap = source.map;
      bodyNormal = source.normalMap;
    }
  });

  root.traverse((node) => {
    if (!(node instanceof Mesh) || !(node.material instanceof MeshStandardMaterial)) return;

    const mesh = node;
    const source = node.material;
    const name = source.name;
    let next: MeshPhysicalMaterial;

    if (name.startsWith("eye")) {
      next = new MeshPhysicalMaterial({
        // the eye in its own pigment: the texture's red-brown ommatidia under a
        // thin wet coat, as the original render lit it
        color: new Color(1, 1, 1),
        map: bodyMap,
        normalMap: bodyNormal,
        aoMap: maps.cavity,
        roughness: 0.5,
        specularIntensity: 0.6,
        clearcoat: 0.35,
        clearcoatRoughness: 0.25,
      });
    } else if (name.startsWith("fur")) {
      // the model's own bristles: the fibre texture's real colour
      next = new MeshPhysicalMaterial({
        color: new Color(1, 1, 1),
        map: source.map,
        alphaTest: 0.4,
        side: DoubleSide,
        roughness: 0.8,
        specularIntensity: 0.3,
      });
    } else if (name.startsWith("wing")) {
      // the model's own membrane: its veins and smoke tint, with the thin-film sheen
      next = new MeshPhysicalMaterial({
        color: new Color(1, 1, 1),
        map: source.map,
        roughnessMap: maps.wingRough,
        transparent: true,
        opacity: 0.6,
        depthWrite: false,
        side: DoubleSide,
        roughness: 1,
        iridescence: 0.35,
        iridescenceIOR: 1.3,
      });
    } else {
      // the cuticle as the original model paints it: matte grey-brown chitin, its
      // roughness and cavity maps, a low specular - no glass, no lacquer. The
      // brain shows when the tour fades the cuticle away, not through it.
      next = new MeshPhysicalMaterial({
        color: new Color(1, 1, 1),
        map: source.map,
        normalMap: source.normalMap,
        roughnessMap: maps.rough,
        aoMap: maps.cavity,
        aoMapIntensity: 1,
        roughness: 1,
        metalness: 0,
        specularIntensity: 0.35,
        envMapIntensity: 0.8,
      });
    }

    next.envMap = environment;
    BASE_OPACITY.set(next, next.opacity);
    mesh.material = next;
    mesh.frustumCulled = false;
    fading.push(next);
  });

  return fading;
}

export class FlyScope {
  readonly #options: ScopeOptions;
  readonly #main: View;
  readonly #observe: View | null;
  readonly #fire: BufferAttribute;
  readonly #hit: BufferAttribute;
  readonly #charge: BufferAttribute;
  readonly #chargeValues: Float32Array;

  readonly #positions: Float32Array;
  readonly #signalGeometry = new BufferGeometry();
  readonly #signalPoints: Points[] = [];
  readonly #flareGeometry = new BufferGeometry();
  #flares: { cell: number; start: number; kind: number }[] = [];
  readonly #flareMaterials: ShaderMaterial[] = [];
  readonly #mixers: AnimationMixer[] = [];
  readonly #actions: { stay: AnimationAction; fly: AnimationAction }[] = [];
  readonly #groomers: Bone[][] = [];
  readonly #fading: Material[] = [];
  readonly #flies: Object3D[] = [];
  readonly #beads: Group[] = [];
  #beadsPlaced = 0;
  #outStart: Int32Array | null = null;
  #outTargets: Uint32Array | null = null;
  #clock = 0;
  #last = 0;
  #tour = 0;
  #paused = false;
  #raf = 0;
  #groomUntil = 0;
  #flyUntil = 0;
  #flyWeight = 0;
  #quality = 2;
  #slow = 0;
  #fast = 0;
  #observeFrame = 0;

  constructor(options: ScopeOptions) {
    this.#options = options;
    const { geometry } = options;
    const slots = geometry.positions.length / 3;

    // the cells: one buffer for both views; a strike written once shows in both
    const positions = new Float32Array(slots * 3);
    const colors = new Float32Array(slots * 3);

    for (let i = 0; i < slots; i += 1) {
      placeNeuron(geometry.positions, i, positions, i * 3);
      colors.set(PALETTE[hash(i) % PALETTE.length], i * 3);
    }

    this.#positions = positions;
    const cellGeometry = new BufferGeometry();

    cellGeometry.setAttribute("position", new BufferAttribute(positions, 3));
    const color = new BufferAttribute(colors, 3);

    cellGeometry.setAttribute("color", color);
    cellGeometry.setAttribute("hue", color);
    this.#fire = new BufferAttribute(new Float32Array(slots), 1);
    this.#hit = new BufferAttribute(new Float32Array(slots), 1);
    this.#chargeValues = new Float32Array(slots);
    this.#charge = new BufferAttribute(this.#chargeValues, 1);
    cellGeometry.setAttribute("fire", this.#fire);
    cellGeometry.setAttribute("hit", this.#hit);
    cellGeometry.setAttribute("charge", this.#charge);
    cellGeometry.computeBoundingSphere();

    this.#main = this.#makeView(options.mount, cellGeometry, 2);
    this.#observe =
      options.observe === null ? null : this.#makeView(options.observe, cellGeometry, 1.25);

    this.#load();
    this.#raf = requestAnimationFrame(this.#step);

    new ResizeObserver(() => this.#resize()).observe(options.mount);

    if (options.observe !== null) new ResizeObserver(() => this.#resize()).observe(options.observe);

    this.#resize();
  }

  get quality(): number {
    return this.#quality;
  }

  #makeView(host: HTMLElement, cells: BufferGeometry, maxRatio: number): View {
    const renderer = new WebGLRenderer({
      antialias: true,
      alpha: false,
      powerPreference: "high-performance",
    });

    renderer.setPixelRatio(Math.min(window.devicePixelRatio, maxRatio));
    renderer.toneMapping = ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1;
    renderer.outputColorSpace = SRGBColorSpace;
    host.append(renderer.domElement);

    const scene = new Scene();
    const pmrem = new PMREMGenerator(renderer);

    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environmentIntensity = 0.55;
    scene.background = gradientBackground();

    // the original render's studio: a warm key, a cool fill, a rim that finds the
    // bristles, and a sky over a warm floor
    const key = new DirectionalLight(0xfff4e6, 2.2);
    const fill = new DirectionalLight(0xdfe8ff, 0.7);
    const rim = new DirectionalLight(0xffffff, 1.4);

    key.position.set(-1.6, 2.6, 1.4);
    fill.position.set(2, 1, -1.5);
    rim.position.set(0.5, 1.2, -2.5);
    scene.add(key, fill, rim, new HemisphereLight(0xeef3ff, 0xc9b8a0, 0.6));
    scene.add(shadowBlob());

    const camera = new PerspectiveCamera(36, 1.6, 0.01, 40);

    const cellMaterial = new ShaderMaterial({
      vertexShader: CELL_VERTEX,
      fragmentShader: CELL_FRAGMENT,
      uniforms: { uTime: { value: 0 }, uScale: { value: 600 }, uSize: { value: 0.0012 } },
    });

    const brain = new Points(cells, cellMaterial);

    brain.frustumCulled = false;
    scene.add(brain);

    const signalMaterial = new ShaderMaterial({
      vertexShader: SIGNAL_VERTEX,
      fragmentShader: SIGNAL_FRAGMENT,
      uniforms: {
        uTime: { value: 0 },
        uScale: { value: 600 },
        uSize: { value: 0.016 },
        uTravel: { value: SIGNAL_TRAVEL },
      },
      transparent: true,
      depthWrite: false,
      depthTest: false,
    });

    const signals = new Points(this.#signalGeometry, signalMaterial);

    signals.frustumCulled = false;
    signals.renderOrder = 2;
    scene.add(signals);
    this.#signalPoints.push(signals);

    const flareMaterial = new ShaderMaterial({
      vertexShader: FLARE_VERTEX,
      fragmentShader: FLARE_FRAGMENT,
      uniforms: { uTime: { value: 0 }, uScale: { value: 600 }, uSize: { value: 0.05 } },
      transparent: true,
      depthWrite: false,
      depthTest: false,
    });

    const flares = new Points(this.#flareGeometry, flareMaterial);

    flares.frustumCulled = false;
    flares.renderOrder = 3;
    scene.add(flares);
    this.#flareMaterials.push(flareMaterial);

    // the beads float around the stage's specimen only; the observation card is a scope
    if (this.#beadsPlaced === 0) {
      const floating = beads();

      scene.add(floating);
      this.#beads.push(floating);
    }

    this.#beadsPlaced += 1;

    return { renderer, scene, camera, host, cells: cellMaterial, signals: signalMaterial };
  }

  #load(): void {
    const loader = new GLTFLoader();
    const maps = loadSurfaceMaps();

    loader.load(`${import.meta.env.BASE_URL}fly/housefly.glb`, (gltf) => {
      const views = [this.#main, this.#observe].filter((v): v is View => v !== null);

      views.forEach((view, i) => {
        const fly = i === 0 ? gltf.scene : cloneSkinned(gltf.scene);
        const environment = view.scene.environment;

        if (environment === null) return;

        const materials = dress(fly, environment, maps);

        // the observation card always shows the whole specimen: only the stage fades
        if (i === 0) this.#fading.push(...materials);

        view.scene.add(fly);
        this.#flies.push(fly);

        const mixer = new AnimationMixer(fly);
        const clip = (name: string) => gltf.animations.find((a) => a.name.includes(name));
        const stayClip = clip("stay");
        const flyClip = clip("|fly|");

        if (stayClip === undefined || flyClip === undefined) return;

        const stay = mixer.clipAction(stayClip);
        const flying = mixer.clipAction(flyClip);

        stay.setLoop(LoopRepeat, Infinity).play();
        flying.setLoop(LoopRepeat, Infinity).play();
        flying.setEffectiveWeight(0);
        this.#mixers.push(mixer);
        this.#actions.push({ stay, fly: flying });

        const bones: Bone[] = [];

        fly.traverse((node) => {
          if (node instanceof Bone && /_leg_4[2-4]$/.test(node.name)) bones.push(node);

          if (node instanceof SkinnedMesh) node.frustumCulled = false;
        });
        this.#groomers.push(bones);
      });
    });
  }

  #resize(): void {
    for (const view of [this.#main, this.#observe]) {
      if (view === null) continue;

      const width = Math.max(1, view.host.clientWidth);
      const height = Math.max(1, view.host.clientHeight);

      view.renderer.setSize(width, height);
      view.camera.aspect = width / height;
      view.camera.updateProjectionMatrix();
      const scale = height / (2 * Math.tan(MathUtils.degToRad(view.camera.fov / 2)));

      view.cells.uniforms.uScale.value = scale;
      view.signals.uniforms.uScale.value = scale;

      for (const material of this.#flareMaterials) material.uniforms.uScale.value = scale;
    }
  }

  setPaused(paused: boolean): void {
    this.#paused = paused;
  }

  /** the tour index (0..6); the camera moves there and resumes the tour from it */
  goTo(index: number): void {
    const cycle = MOVE_SECONDS + HOLD_SECONDS;

    this.#tour = ((index - 1 + STOPS.length) % STOPS.length) * cycle + HOLD_SECONDS;
  }

  /** a feed: the kernel charges the sensory cells n < 256 with n % 60 === channel */
  poke(channel: number): void {
    const now = this.#clock;
    const sensory = Math.min(256, this.#fire.count);

    for (let n = channel % 60; n < sensory; n += 60) {
      this.#hit.setX(n, now + 0.05);
      this.#flares.push({ cell: n, start: now + 0.05, kind: 2 });
    }

    this.#hit.needsUpdate = true;
    this.#buildFlares();
    this.#groomUntil = now + 2.4;
  }

  /** a thought: the fired cells strike, and a signal runs down each of their synapses */
  thought(fired: readonly number[]): void {
    const now = this.#clock;
    const index = this.#outIndex();
    const curves: number[] = [];

    fired.forEach((cell, order) => {
      if (cell >= this.#fire.count) return;

      const at = now + order * 0.06;

      this.#fire.setX(cell, at);
      this.#flares.push({ cell, start: at, kind: 0 });

      const begin = index.start[cell];
      const end = Math.min(index.start[cell + 1], begin + MAX_TARGETS);

      for (let e = begin; e < end; e += 1) {
        const target = index.targets[e];

        const arrival = at + 0.15 + (e - begin) * 0.03 + SIGNAL_TRAVEL;

        curves.push(cell, target, at + 0.15 + (e - begin) * 0.03);
        this.#hit.setX(target, arrival);
        this.#flares.push({ cell: target, start: arrival, kind: 1 });
      }
    });

    this.#fire.needsUpdate = true;
    this.#hit.needsUpdate = true;
    this.#buildSignals(curves);
    this.#buildFlares();

    const motor = fired.some((cell) => this.#options.descending[cell] === 1);

    this.#flyUntil = now + (motor ? 2.6 : 0.9);
    this.#flyWeight = motor ? 1 : 0.45;
  }

  /** the membrane field: cells near the trigger glow faintly, as charge */
  setField(potentials: Int16Array | null, threshold: number): void {
    const charge = this.#chargeValues;

    for (let i = 0; i < charge.length; i += 1) {
      const ratio = potentials === null || i >= potentials.length ? 0 : potentials[i] / threshold;

      // a non-finite ratio is a failed read, not a charge
      charge[i] = Number.isFinite(ratio) && ratio > 0.92 ? Math.min(1, ratio) * 0.4 : 0;
    }

    this.#charge.needsUpdate = true;
  }

  /** world position of a cell, for labels */
  cellPosition(cell: number): Vector3 {
    return new Vector3(
      this.#positions[cell * 3],
      this.#positions[cell * 3 + 1],
      this.#positions[cell * 3 + 2],
    );
  }

  #outIndex(): OutIndex {
    if (this.#outStart !== null && this.#outTargets !== null)
      return { start: this.#outStart, targets: this.#outTargets };

    const { sources, targets } = this.#options.connectome;
    const slots = this.#fire.count;
    const start = new Int32Array(slots + 1);

    for (let e = 0; e < sources.length; e += 1) start[sources[e] + 1] += 1;

    for (let i = 0; i < slots; i += 1) start[i + 1] += start[i];

    const cursor = start.slice(0, slots);
    const sorted = new Uint32Array(sources.length);

    for (let e = 0; e < sources.length; e += 1) {
      sorted[cursor[sources[e]]] = targets[e];
      cursor[sources[e]] += 1;
    }

    this.#outStart = start;
    this.#outTargets = sorted;

    return { start, targets: sorted };
  }

  #buildFlares(): void {
    const now = this.#clock;

    // a flare older than its life is gone; the buffer keeps the newest few thousand
    this.#flares = this.#flares.filter((f) => f.start > now - 4).slice(-FLARE_KEEP);
    const count = this.#flares.length;
    const position = new Float32Array(count * 3);
    const start = new Float32Array(count);
    const kind = new Float32Array(count);

    this.#flares.forEach((flare, i) => {
      position[i * 3] = this.#positions[flare.cell * 3];
      position[i * 3 + 1] = this.#positions[flare.cell * 3 + 1];
      position[i * 3 + 2] = this.#positions[flare.cell * 3 + 2];
      start[i] = flare.start;
      kind[i] = flare.kind;
    });

    const geometry = this.#flareGeometry;

    geometry.setAttribute("position", new BufferAttribute(position, 3));
    geometry.setAttribute("start", new BufferAttribute(start, 1));
    geometry.setAttribute("kind", new BufferAttribute(kind, 1));
    geometry.setDrawRange(0, count);
  }

  #buildSignals(curves: readonly number[]): void {
    const count = (curves.length / 3) * TAIL;
    const p0 = new Float32Array(count * 3);
    const p1 = new Float32Array(count * 3);
    const p2 = new Float32Array(count * 3);
    const start = new Float32Array(count);
    const lag = new Float32Array(count);
    const pos = this.#positions;

    for (let c = 0; c < curves.length / 3; c += 1) {
      const from = curves[c * 3];
      const to = curves[c * 3 + 1];
      const begin = curves[c * 3 + 2];
      const ax = pos[from * 3];
      const ay = pos[from * 3 + 1];
      const az = pos[from * 3 + 2];
      const bx = pos[to * 3];
      const by = pos[to * 3 + 1];
      const bz = pos[to * 3 + 2];
      const span = Math.hypot(bx - ax, by - ay, bz - az);
      // the neurite bows: the control point lifts off the straight path
      const bow = 0.35 * span;
      const mx = (ax + bx) / 2 + (((hash(c) % 200) - 100) / 100) * bow;
      const my = (ay + by) / 2 + bow;
      const mz = (az + bz) / 2 + (((hash(c + 7) % 200) - 100) / 100) * bow;

      for (let k = 0; k < TAIL; k += 1) {
        const i = c * TAIL + k;

        p0.set([ax, ay, az], i * 3);
        p1.set([mx, my, mz], i * 3);
        p2.set([bx, by, bz], i * 3);
        start[i] = begin;
        lag[i] = k * 0.022;
      }
    }

    const geometry = this.#signalGeometry;

    geometry.setAttribute("position", new BufferAttribute(p0, 3));
    geometry.setAttribute("p1", new BufferAttribute(p1, 3));
    geometry.setAttribute("p2", new BufferAttribute(p2, 3));
    geometry.setAttribute("start", new BufferAttribute(start, 1));
    geometry.setAttribute("lag", new BufferAttribute(lag, 1));
    geometry.setDrawRange(0, count);
  }

  #behave(delta: number): void {
    const now = this.#clock;
    const target = now < this.#flyUntil ? this.#flyWeight : 0;

    for (const { stay, fly } of this.#actions) {
      const weight = MathUtils.damp(fly.getEffectiveWeight(), target, 6, delta);

      fly.setEffectiveWeight(weight);
      stay.setEffectiveWeight(1 - weight);
    }

    for (const mixer of this.#mixers) mixer.update(delta);

    // grooming: the front legs rub after a feed, on top of whatever the clip poses
    const groom = Math.max(0, Math.min(1, (this.#groomUntil - now) / 0.6));

    if (groom > 0) {
      const swing = Math.sin(now * 14) * 0.45 * groom;

      for (const bones of this.#groomers) {
        for (const bone of bones) bone.rotation.z += bone.name.startsWith("L") ? swing : -swing;
      }
    }
  }

  #frameOfTour(): TourFrame {
    const cycle = MOVE_SECONDS + HOLD_SECONDS;
    const total = cycle * STOPS.length;
    const time = this.#options.reduced ? HOLD_SECONDS * 0.5 : this.#tour % total;
    const leg = Math.floor(time / cycle);
    const within = time - leg * cycle;
    const from = STOPS[leg];
    const to = STOPS[(leg + 1) % STOPS.length];

    if (within < HOLD_SECONDS) {
      const orbit = within * ORBIT_DEG_PER_SECOND;
      const at = cameraAt(from, orbit);

      return {
        stop: leg,
        moving: false,
        progress: time / total,
        ...at,
        cuticle: from.cuticle,
        dist: from.dist,
        orbit: from.az + orbit,
      };
    }

    const t = ease((within - HOLD_SECONDS) / MOVE_SECONDS);
    const a = cameraAt(from, HOLD_SECONDS * ORBIT_DEG_PER_SECOND);
    const b = cameraAt(to, 0);

    return {
      stop: t < 0.5 ? leg : (leg + 1) % STOPS.length,
      moving: true,
      progress: time / total,
      position: a.position.lerp(b.position, t),
      target: a.target.lerp(b.target, t),
      cuticle: MathUtils.lerp(from.cuticle, to.cuticle, t),
      dist: MathUtils.lerp(from.dist, to.dist, t),
      orbit: MathUtils.lerp(from.az, to.az, t),
    };
  }

  readonly #step = (time: number): void => {
    this.#loop(time);
  };

  #loop(time: number): void {
    this.#raf = requestAnimationFrame(this.#step);

    if (this.#paused || document.hidden) {
      this.#last = time;

      return;
    }

    const started = performance.now();
    const delta = Math.min(0.1, this.#last === 0 ? 0 : (time - this.#last) / 1000);

    this.#last = time;
    this.#clock += delta;
    this.#tour += delta;

    const frame = this.#frameOfTour();
    const camera = this.#main.camera;

    camera.position.copy(frame.position);
    camera.lookAt(frame.target);

    for (const material of this.#fading) {
      const base = BASE_OPACITY.get(material) ?? 1;

      material.opacity = base * frame.cuticle;
      material.visible = frame.cuticle > 0.02;

      // only a fading cuticle sorts as transparent. The flag is compiled into the
      // shader (an opaque program pins alpha to 1), so a change has to recompile -
      // which happens at a transition, never per frame
      const transparent = base < 1 || frame.cuticle < 0.999;

      if (material.transparent !== transparent) {
        material.transparent = transparent;
        material.needsUpdate = true;
      }
    }

    this.#behave(delta);

    for (const group of this.#beads) {
      for (const bead of group.children) {
        bead.position.y += Math.sin(this.#clock * 0.8 + (BEAD_PHASE.get(bead) ?? 0)) * 0.00025;
      }
    }

    for (const view of [this.#main, this.#observe]) {
      if (view === null) continue;

      view.cells.uniforms.uTime.value = this.#clock;
      view.signals.uniforms.uTime.value = this.#clock;
    }

    for (const material of this.#flareMaterials) {
      material.uniforms.uTime.value = this.#clock;
    }

    this.#main.renderer.render(this.#main.scene, camera);

    // the observation card turns the specimen at half the stage's rate
    const observe = this.#observe;

    this.#observeFrame += 1;

    const observeEvery = [6, 2, 1][this.#quality] ?? 1;

    if (observe !== null && this.#observeFrame % observeEvery === 0) {
      const turn = this.#clock * 0.42;
      const center = fromBlender(0, -0.16, 0.28);

      observe.camera.position.set(
        center.x + Math.sin(turn) * 1.5,
        center.y + 0.3,
        center.z - Math.cos(turn) * 1.5,
      );
      observe.camera.lookAt(center);
      observe.renderer.render(observe.scene, observe.camera);
    }

    const height = this.#main.host.clientHeight;
    const focal = height / (2 * Math.tan(MathUtils.degToRad(camera.fov / 2)));
    const pxPerUnit = focal / Math.max(0.0001, frame.position.distanceTo(frame.target));

    this.#options.onFrame?.(
      {
        stop: STOPS[frame.stop].key,
        index: frame.stop,
        progress: frame.progress,
        moving: frame.moving,
        zoom: STOPS[0].dist / frame.dist,
        cuticle: frame.cuticle,
        parallax: ((frame.orbit % 360) + 360) % 360,
        pxPerMm: pxPerUnit / MM_PER_UNIT,
      },
      (p) => this.#project(p),
    );

    this.#budget(performance.now() - started);
  }

  /** observe-card turn angle, degrees */
  get turn(): number {
    return ((((this.#clock * 0.42 * 180) / Math.PI) % 360) + 360) % 360;
  }

  #project(p: Vector3): [number, number, boolean] {
    const v = p.clone().project(this.#main.camera);
    const w = this.#main.host.clientWidth;
    const h = this.#main.host.clientHeight;

    return [
      ((v.x + 1) / 2) * w,
      ((1 - v.y) / 2) * h,
      v.z < 1 && Math.abs(v.x) < 1.1 && Math.abs(v.y) < 1.1,
    ];
  }

  /** a slow device gives back resolution before it gives back the tour */
  #budget(workMs: number): void {
    // one frame that stalls the page is evidence enough; a merely slow one has to repeat
    if (workMs > 80) this.#slow += 46;
    else if (workMs > 28) this.#slow += 1;
    else this.#slow = Math.max(0, this.#slow - 1);

    if (workMs < 10) this.#fast += 1;
    else this.#fast = 0;

    let next = this.#quality;

    if (this.#slow > 45 && this.#quality > 0) next = this.#quality - 1;

    if (this.#fast > 600 && this.#quality < 2) next = this.#quality + 1;

    if (next === this.#quality) return;

    this.#quality = next;
    this.#slow = 0;
    this.#fast = 0;
    const ratio = [0.6, 1, Math.min(window.devicePixelRatio, 2)][next];

    this.#main.renderer.setPixelRatio(ratio);

    this.#resize();
    this.#options.onQuality?.(next);
  }

  dispose(): void {
    cancelAnimationFrame(this.#raf);
  }
}
